import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { useEstates } from '../lib/useEstates';
import { useUpload } from '../lib/useUpload';
import { Modal, Alert, UploadProgress, inputClass, labelClass, primaryBtn, secondaryBtn } from './ui';

interface SectionField {
  key: string;
  label: string;
  type: 'number' | 'text' | 'textarea' | 'select';
  placeholder?: string;
  options?: string[];
}

interface SectionDef {
  key: string;
  label: string;
  fields: SectionField[];
}

export const DAILY_SECTIONS: SectionDef[] = [
  {
    key: 'diesel_log', label: 'Diesel Log',
    fields: [
      { key: 'remaining_liters', label: 'Remaining Liters', type: 'number', placeholder: 'e.g. 420' },
      { key: 'notes', label: 'Usage Notes', type: 'textarea', placeholder: 'Fuel usage notes...' },
    ],
  },
  {
    key: 'generator_log', label: 'Generator Log',
    fields: [
      { key: 'run_hours', label: 'Run Hours', type: 'number', placeholder: 'e.g. 6.5' },
      { key: 'fuel_level', label: 'Fuel Level (%)', type: 'number', placeholder: 'e.g. 65' },
      { key: 'status', label: 'Status', type: 'select', options: ['Operational', 'Running', 'Needs Maintenance', 'Fault'] },
      { key: 'service_note', label: 'Service Note', type: 'textarea', placeholder: 'Generator condition / service notes...' },
    ],
  },
  {
    key: 'ekedc_unit', label: 'EKEDC Unit',
    fields: [
      { key: 'remaining_units', label: 'Remaining Units (kWh)', type: 'number', placeholder: 'e.g. 1250' },
      { key: 'notes', label: 'Usage Notes', type: 'textarea', placeholder: 'Power usage notes...' },
    ],
  },
  {
    key: 'security_report', label: 'Security Report',
    fields: [
      { key: 'severity', label: 'Severity', type: 'select', options: ['Low', 'Medium', 'High', 'Critical'] },
      { key: 'location', label: 'Location', type: 'text', placeholder: 'Where it occurred' },
      { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Describe the security report...' },
    ],
  },
  {
    key: 'electrical', label: 'Electrical',
    fields: [
      { key: 'issue_type', label: 'Issue Type', type: 'select', options: ['Fault', 'Maintenance', 'Installation', 'Inspection'] },
      { key: 'location', label: 'Location', type: 'text', placeholder: 'Area/zone affected' },
      { key: 'details', label: 'Details', type: 'textarea', placeholder: 'Describe electrical issue or work done...' },
    ],
  },
  {
    key: 'plumbing', label: 'Plumbing',
    fields: [
      { key: 'issue_type', label: 'Issue Type', type: 'select', options: ['Leak', 'Burst', 'Blockage', 'Maintenance', 'Inspection'] },
      { key: 'location', label: 'Location', type: 'text', placeholder: 'Area/fixture affected' },
      { key: 'details', label: 'Details', type: 'textarea', placeholder: 'Describe plumbing issue or work done...' },
    ],
  },
  {
    key: 'swimming_pool', label: 'Swimming Pool',
    fields: [
      { key: 'status', label: 'Status', type: 'select', options: ['Operational', 'Needs Attention', 'Closed'] },
      { key: 'ph_level', label: 'pH Level', type: 'text', placeholder: 'e.g. 7.4' },
      { key: 'details', label: 'Details', type: 'textarea', placeholder: 'Pool condition, cleaning notes...' },
    ],
  },
  {
    key: 'cleaning', label: 'Cleaning',
    fields: [
      { key: 'area', label: 'Area', type: 'text', placeholder: 'Area cleaned' },
      { key: 'status', label: 'Status', type: 'select', options: ['Completed', 'In Progress', 'Pending'] },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Cleaning details...' },
    ],
  },
  {
    key: 'waste_disposal', label: 'Waste Disposal',
    fields: [
      { key: 'disposal_type', label: 'Disposal Type', type: 'select', options: ['Regular', 'Special', 'Hazardous'] },
      { key: 'quantity', label: 'Quantity', type: 'text', placeholder: 'e.g. 5 bags, 2 bins' },
      { key: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Disposal details...' },
    ],
  },
  {
    key: 'fumigation', label: 'Fumigation',
    fields: [
      { key: 'area_treated', label: 'Area Treated', type: 'text', placeholder: 'Areas fumigated' },
      { key: 'chemical_used', label: 'Chemical Used', type: 'text', placeholder: 'Product name' },
      { key: 'details', label: 'Details', type: 'textarea', placeholder: 'Treatment notes...' },
    ],
  },
  {
    key: 'water_treatment', label: 'Water Treatment',
    fields: [
      { key: 'reading', label: 'Reading', type: 'number', placeholder: 'e.g. chlorine level' },
      { key: 'treatment_type', label: 'Treatment Type', type: 'text', placeholder: 'e.g. Chlorination, Filtration' },
      { key: 'details', label: 'Details', type: 'textarea', placeholder: 'Treatment notes...' },
    ],
  },
  {
    key: 'sewage_maintenance', label: 'Sewage Maintenance',
    fields: [
      { key: 'inspection_status', label: 'Inspection Status', type: 'select', options: ['OK', 'Needs Attention', 'Blockage', 'Overflow'] },
      { key: 'details', label: 'Details', type: 'textarea', placeholder: 'Sewage system condition...' },
    ],
  },
  {
    key: 'road_drainage', label: 'Road & Drainage Maintenance',
    fields: [
      { key: 'condition', label: 'Condition', type: 'select', options: ['Good', 'Fair', 'Poor', 'Critical'] },
      { key: 'location', label: 'Location', type: 'text', placeholder: 'Specific road/drain' },
      { key: 'details', label: 'Details', type: 'textarea', placeholder: 'Maintenance notes...' },
    ],
  },
  {
    key: 'common_area', label: 'Common Area',
    fields: [
      { key: 'location', label: 'Location', type: 'text', placeholder: 'Which common area' },
      { key: 'status', label: 'Status', type: 'select', options: ['OK', 'Needs Maintenance', 'Damaged'] },
      { key: 'details', label: 'Details', type: 'textarea', placeholder: 'General observations...' },
    ],
  },
];

export const INCIDENCE_CATEGORIES = [
  'Security',
  'Electrical',
  'Plumbing',
  'Water Treatment',
  'Common Area',
  'Generator',
];

export interface DailySectionView {
  label: string;
  lines: string[];
  attachmentIndices: number[];
}

export function parseDailyReport(content: string, attachments: { id: number; filename: string; url: string; mimetype: string; size: number }[]): DailySectionView[] {
  const sections: DailySectionView[] = [];
  const sectionMap = new Map<string, DailySectionView>();

  const headerRegex = /^\[([^\]]+)\]\s*$/gm;
  let match: RegExpExecArray | null;
  const headerPositions: { label: string; start: number; end: number }[] = [];

  while ((match = headerRegex.exec(content)) !== null) {
    headerPositions.push({ label: match[1], start: match.index + match[0].length, end: -1 });
  }

  for (let i = 0; i < headerPositions.length; i++) {
    headerPositions[i].end = i + 1 < headerPositions.length ? headerPositions[i + 1].start - 1 : content.length;
  }

  for (const hp of headerPositions) {
    const body = content.slice(hp.start, hp.end).trim();
    const lines = body.split('\n').filter((l) => l.trim());
    const view: DailySectionView = { label: hp.label, lines, attachmentIndices: [] };
    sections.push(view);
    sectionMap.set(hp.label.toLowerCase(), view);
  }

  attachments.forEach((att, idx) => {
    const prefixMatch = att.filename.match(/^\[([^\]]+)\]\s*/);
    if (prefixMatch) {
      const sectionLabel = prefixMatch[1].toLowerCase();
      const section = sectionMap.get(sectionLabel);
      if (section) {
        section.attachmentIndices.push(idx);
      }
    }
  });

  if (sections.length === 0 && content.trim()) {
    sections.push({ label: 'Report', lines: content.split('\n').filter((l) => l.trim()), attachmentIndices: attachments.map((_, i) => i) });
  }

  return sections;
}

interface SectionState {
  fieldValues: Record<string, string>;
  files: File[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  estate?: string;
}

const emptySections = (): Record<string, SectionState> =>
  Object.fromEntries(DAILY_SECTIONS.map((s) => [s.key, { fieldValues: {}, files: [] }]));

export default function DailyReportForm({ open, onClose, onSubmitted, estate }: Props) {
  const { getBlocksForEstate } = useEstates();
  const blocks = estate ? getBlocksForEstate(estate) : [];
  const [sections, setSections] = useState<Record<string, SectionState>>(emptySections);
  const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));
  const upload = useUpload();
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<'daily' | 'incidence'>('daily');

  const [incidenceCategory, setIncidenceCategory] = useState('');
  const [incidenceSeverity, setIncidenceSeverity] = useState('');
  const [incidenceDetails, setIncidenceDetails] = useState('');
  const [incidenceFiles, setIncidenceFiles] = useState<File[]>([]);

  const toggleSection = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setField = (sectionKey: string, fieldKey: string, value: string) => {
    setSections((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        fieldValues: { ...prev[sectionKey].fieldValues, [fieldKey]: value },
      },
    }));
  };

  const setFiles = (sectionKey: string, files: File[]) => {
    setSections((prev) => ({
      ...prev,
      [sectionKey]: { ...prev[sectionKey], files },
    }));
  };

  const hasContent = (sectionKey: string) => {
    const s = sections[sectionKey];
    return Object.values(s.fieldValues).some((v) => v && v.trim()) || s.files.length > 0;
  };

  const buildSectionContent = (section: SectionDef, state: SectionState): string => {
    const lines: string[] = [];
    for (const field of section.fields) {
      const val = state.fieldValues[field.key];
      if (val && val.trim()) {
        lines.push(`${field.label}: ${val.trim()}`);
      }
    }
    if (section.key === 'ekedc_unit') {
      if (state.fieldValues['block']) lines.push(`Block: ${state.fieldValues['block']}`);
      if (state.fieldValues['common_area']) lines.push(`Common Area: ${state.fieldValues['common_area']}`);
    }
    return lines.join('\n');
  };

  const buildFullContent = (): string => {
    return DAILY_SECTIONS
      .filter((s) => hasContent(s.key))
      .map((s) => {
        const body = buildSectionContent(s, sections[s.key]);
        return `[${s.label}]\n${body}`;
      })
      .join('\n\n');
  };

  const handleSubmitDaily = async (e: React.FormEvent) => {
    e.preventDefault();
    const anyFilled = DAILY_SECTIONS.some((s) => hasContent(s.key));
    if (!anyFilled) {
      setError('Please fill in at least one section.');
      return;
    }
    upload.reset();
    setError('');
    try {
      const content = buildFullContent();
      const fd = new FormData();
      fd.append('report_type', 'daily');
      fd.append('content', content);
      fd.append('report_date', reportDate);
      fd.append('heading', 'Daily Report');
      if (estate) fd.append('facility', estate);
      DAILY_SECTIONS.forEach((s) => {
        sections[s.key].files.forEach((f) => {
          const prefixed = new File([f], `[${s.label}] ${f.name}`, { type: f.type });
          fd.append('attachments', prefixed);
        });
      });
      await upload.run('/reports', fd);
      onSubmitted();
      onClose();
      resetForm();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleSubmitIncidence = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!incidenceCategory) {
      setError('Please select a category.');
      return;
    }
    if (!incidenceSeverity) {
      setError('Please select a severity level.');
      return;
    }
    if (!incidenceDetails.trim()) {
      setError('Please describe the incident.');
      return;
    }
    upload.reset();
    setError('');
    try {
      const fd = new FormData();
      fd.append('report_type', 'incident');
      fd.append('category', incidenceCategory.toLowerCase());
      fd.append('severity', incidenceSeverity);
      fd.append('content', incidenceDetails);
      fd.append('report_date', reportDate);
      fd.append('heading', `Incident Report - ${incidenceCategory}`);
      if (estate) fd.append('facility', estate);
      incidenceFiles.forEach((f) => fd.append('attachments', f));
      await upload.run('/reports', fd);
      onSubmitted();
      onClose();
      resetForm();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const resetForm = () => {
    setSections(emptySections());
    setReportDate(new Date().toISOString().slice(0, 10));
    setIncidenceCategory('');
    setIncidenceSeverity('');
    setIncidenceDetails('');
    setIncidenceFiles([]);
    setExpanded({});
    upload.reset();
    setError('');
    setActiveTab('daily');
  };

  return (
    <Modal open={open} onClose={() => { resetForm(); onClose(); }} title="Daily Report">
      {error && <Alert>{error}</Alert>}

      <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('daily')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeTab === 'daily' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Daily Report
        </button>
        <button
          onClick={() => setActiveTab('incidence')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${activeTab === 'incidence' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Incident Report
        </button>
      </div>

      <div className="mb-4">
        <label className={labelClass}>Report Date</label>
        <input type="date" className={inputClass} value={reportDate} onChange={(e) => setReportDate(e.target.value)} />
      </div>

      {activeTab === 'daily' ? (
        <form onSubmit={handleSubmitDaily} className="space-y-3">
          {DAILY_SECTIONS.map((section) => {
            const isOpen = expanded[section.key];
            const filled = hasContent(section.key);
            const state = sections[section.key];
            return (
              <div key={section.key} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection(section.key)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition text-left"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-800">
                    <FileText className="h-4 w-4 text-gray-400" />
                    {section.label}
                    {filled && <span className="w-2 h-2 rounded-full bg-primary inline-block" />}
                  </span>
                  {isOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </button>
                {isOpen && (
                  <div className="p-4 space-y-3 bg-white">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {section.fields.map((field) => {
                        if (field.type === 'textarea') {
                          return (
                            <div key={field.key} className="sm:col-span-2">
                              <label className={labelClass}>{field.label}</label>
                              <textarea
                                className={inputClass}
                                rows={3}
                                value={state.fieldValues[field.key] || ''}
                                onChange={(e) => setField(section.key, field.key, e.target.value)}
                                placeholder={field.placeholder}
                              />
                            </div>
                          );
                        }
                        if (field.type === 'select') {
                          return (
                            <div key={field.key}>
                              <label className={labelClass}>{field.label}</label>
                              <select
                                className={inputClass}
                                value={state.fieldValues[field.key] || ''}
                                onChange={(e) => setField(section.key, field.key, e.target.value)}
                              >
                                <option value="">Select...</option>
                                {field.options?.map((o) => <option key={o} value={o}>{o}</option>)}
                              </select>
                            </div>
                          );
                        }
                        return (
                          <div key={field.key}>
                            <label className={labelClass}>{field.label}</label>
                            <input
                              type={field.type}
                              className={inputClass}
                              value={state.fieldValues[field.key] || ''}
                              onChange={(e) => setField(section.key, field.key, e.target.value)}
                              placeholder={field.placeholder}
                            />
                          </div>
                        );
                      })}
                    </div>
                    {section.key === 'ekedc_unit' && blocks.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className={labelClass}>Block</label>
                          <select
                            className={inputClass}
                            value={state.fieldValues['block'] || ''}
                            onChange={(e) => setField(section.key, 'block', e.target.value)}
                          >
                            <option value="">Select block...</option>
                            {blocks.map((b) => <option key={b} value={`Block ${b}`}>Block {b}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={labelClass}>Common Area</label>
                          <select
                            className={inputClass}
                            value={state.fieldValues['common_area'] || ''}
                            onChange={(e) => setField(section.key, 'common_area', e.target.value)}
                          >
                            <option value="">Select...</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                        </div>
                      </div>
                    )}
                    <div>
                      <label className={labelClass}>Upload Media (up to 15)</label>
                      <input
                        type="file"
                        multiple
                        max={15}
                        accept="image/*,video/*,.pdf"
                        className={inputClass}
                        onChange={(e) => {
                          const selected = Array.from(e.target.files || []);
                          setFiles(section.key, selected.slice(0, 15));
                        }}
                      />
                      {state.files.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">{state.files.length} / 15 file(s) selected</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <UploadProgress
            percent={upload.percent}
            phase={upload.phase}
            error={upload.error}
            onCancel={upload.cancel}
          />
          <div className="flex justify-end space-x-3 pt-3">
            <button type="button" onClick={() => { resetForm(); onClose(); }} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={upload.busy} className={primaryBtn}>
              {upload.phase === 'uploading'
                ? `Uploading ${upload.percent}%`
                : upload.phase === 'processing' ? 'Processing...' : 'Submit Daily Report'}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSubmitIncidence} className="space-y-4">
          <div>
            <label className={labelClass}>Category *</label>
            <select className={inputClass} required value={incidenceCategory} onChange={(e) => setIncidenceCategory(e.target.value)}>
              <option value="">Select category...</option>
              {INCIDENCE_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Severity *</label>
            <select className={inputClass} required value={incidenceSeverity} onChange={(e) => setIncidenceSeverity(e.target.value)}>
              <option value="">Select severity...</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Incident Details *</label>
            <textarea
              className={inputClass}
              rows={5}
              required
              value={incidenceDetails}
              onChange={(e) => setIncidenceDetails(e.target.value)}
              placeholder="Describe the incident in detail..."
            />
          </div>
          <div>
            <label className={labelClass}>Upload Media (up to 15)</label>
            <input
              type="file"
              multiple
              max={15}
              accept="image/*,video/*,.pdf"
              className={inputClass}
              onChange={(e) => setIncidenceFiles(Array.from(e.target.files || []).slice(0, 15))}
            />
{incidenceFiles.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{incidenceFiles.length} / 15 file(s) selected</p>
              )}
          </div>
          <UploadProgress
            percent={upload.percent}
            phase={upload.phase}
            error={upload.error}
            onCancel={upload.cancel}
          />
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => { resetForm(); onClose(); }} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={upload.busy} className={primaryBtn}>
              {upload.phase === 'uploading'
                ? `Uploading ${upload.percent}%`
                : upload.phase === 'processing' ? 'Processing...' : 'Submit Incident Report'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}


